import type { ApiResponse } from '@/types/global'
import type {
  Period,
  ProcessedStatistics,
  SurveyStatisticsParams,
  SurveyStatisticsRaw
} from '@/types/survey'
import axios from '../plugins/axios'

// Survey API Configuration
const SURVEY_ID = 24

/**
 * Calculate check_token for API authentication
 * Formula: survey_id × current_hour_HHMM × 272333
 *
 * @returns Calculated check token
 *
 * @example
 * // At 9:30 AM Riyadh time
 * calculateCheckToken() // Returns: 6079057440
 */
export const calculateCheckToken = (): number => {
  // Get current time in Riyadh timezone (Asia/Riyadh)
  const riyadhTime = new Date(
    new Date().toLocaleString('en-US', { timeZone: 'Asia/Riyadh' })
  )

  // Extract hours and minutes
  const hours = riyadhTime.getHours()
  const minutes = riyadhTime.getMinutes()

  // Format as HHMM string with leading zeros, then parse as integer
  const timeString = hours.toString().padStart(2, '0') + minutes.toString().padStart(2, '0')
  const timeInteger = parseInt(timeString, 10)

  // Calculate token: survey_id × current_hour_HHMM × 272333
  return SURVEY_ID * timeInteger * 272333
}

/**
 * Fetch survey statistics from the API
 *
 * @param year - The year to fetch statistics for
 * @param branch - Optional branch ID filter
 * @param mosque - Optional mosque ID filter
 * @returns Raw survey statistics from API
 *
 * @example
 * // Fetch all branches for 2024
 * const stats = await fetchSurveyStatistics(2024)
 *
 * @example
 * // Fetch specific branch for 2024
 * const stats = await fetchSurveyStatistics(2024, 5)
 *
 * @example
 * // Fetch specific mosque for 2024
 * const stats = await fetchSurveyStatistics(2024, undefined, 101)
 */
export const fetchSurveyStatistics = async (
  year: number,
  branch?: number,
  mosque?: number
): Promise<SurveyStatisticsRaw> => {
  // Build request params
  const params: SurveyStatisticsParams = {
    survey_id: SURVEY_ID,
    check_token: calculateCheckToken(),
    year
  }

  // Add filters based on user selection
  // Rule: Only include branch if mosque is not selected
  // Rule: Include mosque if selected (mosque filter takes precedence)
  if (mosque !== undefined) {
    params.mosque = mosque
  } else if (branch !== undefined) {
    params.branch = branch
  }

  const response = await axios.post<ApiResponse<{ data: SurveyStatisticsRaw }>>(
    '/api/survey/dashboard_api/fetch',
    {
      jsonrpc: '2.0',
      params
    }
  )

  if (response.data.error) {
    throw new Error(response.data.error.message)
  }

  return response.data.result.data
}

/**
 * Transform raw API data into period-indexed structure
 * Converts flat arrays into organized objects for easier UI consumption
 *
 * @param rawData - Raw survey statistics from API
 * @returns Processed statistics indexed by period
 *
 * @example
 * const raw = await fetchSurveyStatistics(2024)
 * const processed = transformSurveyStatistics(raw)
 * // Access year data: processed.year.basicData.participants_count
 */
export const transformSurveyStatistics = (
  rawData: SurveyStatisticsRaw
): ProcessedStatistics => {
  // Initialize all periods with empty data
  const allPeriods: Period[] = [
    'year',
    'half1',
    'half2',
    'quarter1',
    'quarter2',
    'quarter3',
    'quarter4',
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december'
  ]

  const result: ProcessedStatistics = {} as ProcessedStatistics

  // Initialize all periods with default empty data
  allPeriods.forEach((period) => {
    result[period] = {
      basicData: {
        participants_count: 0,
        participants_percentage: 0,
        comments_count: 0
      },
      starsData: [],
      reasonsData: []
    }
  })

  // Process basic_data
  rawData.basic_data.forEach(([period, participants_count, participants_percentage, comments_count]) => {
    result[period].basicData = {
      participants_count,
      participants_percentage,
      comments_count
    }
  })

  // Process votes_comments_data (stars data)
  // Group by period
  rawData.votes_comments_data.forEach(([stars_count, period, participants_count, comments_count]) => {
    result[period].starsData.push({
      stars_count,
      participants_count,
      comments_count
    })
  })

  // Process reasons_data
  // Group by period
  rawData.reasons_data.forEach(([period, reason_name, count]) => {
    result[period].reasonsData.push({
      reason_name,
      count
    })
  })

  return result
}

/**
 * Get available years for year picker
 * Returns array from 2024 to current year (inclusive)
 *
 * @returns Array of available years
 *
 * @example
 * // In 2025
 * getAvailableYears() // Returns: [2024, 2025]
 */
export const getAvailableYears = (): number[] => {
  const currentYear = new Date().getFullYear()
  const startYear = 2024
  const years: number[] = []

  for (let year = startYear; year <= currentYear; year++) {
    years.push(year)
  }

  return years
}

/**
 * Detect the period type from a date range
 * Analyzes the start and end dates to determine which period it represents
 *
 * @param startDate - Start date of the range
 * @param endDate - End date of the range
 * @returns The detected period type, or null if no match
 *
 * @example
 * detectPeriodFromRange(new Date('2024-01-01'), new Date('2024-12-31')) // Returns: 'year'
 * detectPeriodFromRange(new Date('2024-01-01'), new Date('2024-06-30')) // Returns: 'half1'
 * detectPeriodFromRange(new Date('2024-05-01'), new Date('2024-05-31')) // Returns: 'may'
 */
export const detectPeriodFromRange = (
  startDate: Date | string,
  endDate: Date | string
): Period | null => {
  const start = new Date(startDate)
  const end = new Date(endDate)

  const startMonth = start.getMonth() // 0-11
  const endMonth = end.getMonth() // 0-11
  const startDay = start.getDate()

  // Helper to check if it's the last day of a month
  const isEndOfMonth = (date: Date) => {
    const nextDay = new Date(date)
    nextDay.setDate(date.getDate() + 1)
    return nextDay.getMonth() !== date.getMonth()
  }

  // Check for full year (Jan 1 - Dec 31)
  if (startMonth === 0 && startDay === 1 && endMonth === 11 && isEndOfMonth(end)) {
    return 'year'
  }

  // Check for first half (Jan 1 - Jun 30)
  if (startMonth === 0 && startDay === 1 && endMonth === 5 && isEndOfMonth(end)) {
    return 'half1'
  }

  // Check for second half (Jul 1 - Dec 31)
  if (startMonth === 6 && startDay === 1 && endMonth === 11 && isEndOfMonth(end)) {
    return 'half2'
  }

  // Check for quarters
  // Q1: Jan 1 - Mar 31
  if (startMonth === 0 && startDay === 1 && endMonth === 2 && isEndOfMonth(end)) {
    return 'quarter1'
  }
  // Q2: Apr 1 - Jun 30
  if (startMonth === 3 && startDay === 1 && endMonth === 5 && isEndOfMonth(end)) {
    return 'quarter2'
  }
  // Q3: Jul 1 - Sep 30
  if (startMonth === 6 && startDay === 1 && endMonth === 8 && isEndOfMonth(end)) {
    return 'quarter3'
  }
  // Q4: Oct 1 - Dec 31
  if (startMonth === 9 && startDay === 1 && endMonth === 11 && isEndOfMonth(end)) {
    return 'quarter4'
  }

  // Check for individual months (first day to last day of same month)
  if (startMonth === endMonth && startDay === 1 && isEndOfMonth(end)) {
    const monthMap: Record<number, Period> = {
      0: 'january',
      1: 'february',
      2: 'march',
      3: 'april',
      4: 'may',
      5: 'june',
      6: 'july',
      7: 'august',
      8: 'september',
      9: 'october',
      10: 'november',
      11: 'december'
    }
    return monthMap[startMonth]
  }

  // No matching period found
  return null
}
